// Firebase Authentication Service
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  User,
  UserCredential
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from '../lib/firebase';
import { SecurityUtils, InputValidator } from '../utils/security';

const GENERIC_RESET_MESSAGE =
  'If an account exists for that email, a password reset link has been sent. Check your inbox and spam folder.';

export interface AuthUser {
  uid: string;
  email: string;
  name: string;
  phone?: string;
  role: 'user' | 'admin' | 'super_admin';
  createdAt: Date;
  lastLoginAt: Date;
  /** Set only on accounts created after email verification was required. */
  verificationRequired?: boolean;
}

export interface AuthError {
  code: string;
  message: string;
}

export interface AuthResult {
  success: boolean;
  user?: AuthUser;
  error?: AuthError;
  /** Present after signup when a verification email was queued. */
  verificationEmailSent?: boolean;
  /** Generic / server message for password-reset or verification flows. */
  message?: string;
}

class AuthService {
  // Sign up with email and password
  async signUp(email: string, password: string, name: string, phone?: string): Promise<AuthResult> {
    try {
      // Validate input
      const validation = InputValidator.validateUserRegistration({
        email,
        password,
        confirmPassword: password,
        name
      });

      if (!validation.isValid) {
        return {
          success: false,
          error: {
            code: 'validation-error',
            message: validation.errors.join(', ')
          }
        };
      }

      // Sanitize inputs
      const sanitizedEmail = SecurityUtils.sanitizeString(email);
      const sanitizedName = SecurityUtils.sanitizeString(name);
      const sanitizedPhone = phone && phone.trim() !== '' ? SecurityUtils.sanitizeString(phone) : undefined;

      // Create user account
      const userCredential: UserCredential = await createUserWithEmailAndPassword(
        auth, 
        sanitizedEmail, 
        password
      );

      // Update user profile
      await updateProfile(userCredential.user, {
        displayName: sanitizedName
      });

      // Create user document in Firestore
      const userData: any = {
        uid: userCredential.user.uid,
        email: sanitizedEmail,
        name: sanitizedName,
        role: 'user',
        createdAt: new Date(),
        lastLoginAt: new Date(),
        verificationRequired: true,
      };

      // Only add phone if it has a value
      if (sanitizedPhone && sanitizedPhone.trim() !== '') {
        userData.phone = sanitizedPhone;
      }

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      // Branded verification email via Cloud Function + Trigger Email SMTP.
      // Account creation still succeeds if delivery fails.
      let verificationEmailSent = false;
      try {
        const verifyResult = await this.requestEmailVerification();
        verificationEmailSent = verifyResult.success;
      } catch (verificationError) {
        console.error('Failed to send verification email:', verificationError);
      }

      return {
        success: true,
        user: userData as AuthUser,
        verificationEmailSent,
      };

    } catch (error: any) {
      console.error('Sign up error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getErrorMessage(error.code)
        }
      };
    }
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      console.log('🔐 Starting sign in process for:', email);
      
      // Validate inputs
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // Sanitize email
      const sanitizedEmail = SecurityUtils.sanitizeString(email);
      console.log('📧 Sanitized email:', sanitizedEmail);

      // Check Firebase auth state
      console.log('🔥 Firebase auth state:', auth.currentUser ? 'User logged in' : 'No user');

      // Sign in user
      console.log('🚀 Attempting Firebase sign in...');
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        auth, 
        sanitizedEmail, 
        password
      );
      console.log('✅ Firebase sign in successful:', userCredential.user.uid);

      // Get user data from Firestore
      console.log('📄 Fetching user data from Firestore...');
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        console.error('❌ User document not found in Firestore');
        throw new Error('User document not found');
      }

      const userData = userDoc.data() as AuthUser;
      console.log('✅ User data retrieved:', userData);
      
      // Update last login time
      const updatedUserData = {
        ...userData,
        lastLoginAt: new Date()
      };

      console.log('💾 Updating last login time...');
      await setDoc(doc(db, 'users', userCredential.user.uid), updatedUserData);
      console.log('✅ Sign in process completed successfully');

      return {
        success: true,
        user: updatedUserData
      };

    } catch (error: any) {
      console.error('❌ Sign in error details:');
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error object:', error);
      
      // Check for specific network-related errors
      if (error.code === 'auth/network-request-failed') {
        console.error('🌐 Network request failed - this could be:');
        console.error('1. Internet connectivity issues');
        console.error('2. Firebase service outage');
        console.error('3. Firewall/proxy blocking requests');
        console.error('4. DNS resolution issues');
      }
      
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getErrorMessage(error.code)
        }
      };
    }
  }

  // Sign out
  async signOut(): Promise<AuthResult> {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error: any) {
      console.error('Sign out error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getErrorMessage(error.code)
        }
      };
    }
  }

  /**
   * Request a branded password-reset email via Cloud Function.
   * Always returns a generic success message (no account enumeration).
   * Transient transport failures also map to the generic success message so
   * the forgot-password UX never dead-ends on infrastructure blips.
   */
  async resetPassword(email: string): Promise<AuthResult> {
    try {
      if (!SecurityUtils.isValidEmail(email)) {
        return {
          success: false,
          error: {
            code: 'invalid-email',
            message: 'Please enter a valid email address'
          }
        };
      }

      const sanitizedEmail = SecurityUtils.sanitizeString(email).toLowerCase();
      const callable = httpsCallable<{ email: string }, { success: boolean; message?: string }>(
        functions,
        'requestPasswordReset'
      );

      try {
        const response = await callable({ email: sanitizedEmail });
        return {
          success: true,
          message: response.data?.message || GENERIC_RESET_MESSAGE,
        };
      } catch (firstError: any) {
        // One automatic retry for cold-start / transient Cloud Run failures.
        if (firstError?.code === 'functions/invalid-argument') {
          throw firstError;
        }
        console.warn('Password reset callable failed, retrying once:', firstError?.code || firstError);
        await new Promise((resolve) => setTimeout(resolve, 700));
        const response = await callable({ email: sanitizedEmail });
        return {
          success: true,
          message: response.data?.message || GENERIC_RESET_MESSAGE,
        };
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      if (error?.code === 'functions/invalid-argument') {
        return {
          success: false,
          error: {
            code: 'invalid-email',
            message: 'Please enter a valid email address',
          },
        };
      }
      // Best-practice UX for forgot-password: do not expose infrastructure
      // failures that could also enable probing. Ask the user to check mail.
      return {
        success: true,
        message: GENERIC_RESET_MESSAGE,
      };
    }
  }

  /**
   * Firebase Auth emailVerified flags for accounts an admin can already see.
   * Missing ids are omitted when Auth has no matching user.
   */
  async getEmailVerificationStatuses(
    uids: string[]
  ): Promise<{ success: boolean; statuses: Record<string, boolean>; error?: string }> {
    if (uids.length === 0) return { success: true, statuses: {} };
    const callable = httpsCallable<{ uids: string[] }, { statuses: Record<string, boolean> }>(
      functions,
      'getEmailVerificationStatuses'
    );
    const statuses: Record<string, boolean> = {};
    try {
      for (let i = 0; i < uids.length; i += 500) {
        const response = await callable({ uids: uids.slice(i, i + 500) });
        Object.assign(statuses, response.data?.statuses ?? {});
      }
      return { success: true, statuses };
    } catch (error: any) {
      console.error('Email verification status error:', error);
      return {
        success: false,
        statuses: {},
        error: 'Could not load email verification status.',
      };
    }
  }

  /** Queue a branded email-verification message for the signed-in user. */
  async requestEmailVerification(): Promise<AuthResult> {
    try {
      const callable = httpsCallable<
        Record<string, never>,
        { success: boolean; message?: string; alreadyVerified?: boolean }
      >(functions, 'requestEmailVerification');
      const response = await callable({});
      return {
        success: true,
        message: response.data?.message || 'Verification email sent. Check your inbox and spam folder.',
      };
    } catch (error: any) {
      console.error('Email verification request error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message:
            error?.message ||
            'Unable to send verification email. Please try again later.',
        },
      };
    }
  }

  /** Validate a password-reset oobCode and return the associated email. */
  async verifyPasswordResetCode(oobCode: string): Promise<AuthResult & { email?: string }> {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode);
      return { success: true, email };
    } catch (error: any) {
      console.error('Verify password reset code error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getActionCodeErrorMessage(error.code),
        },
      };
    }
  }

  /** Complete password reset with a new password. */
  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<AuthResult> {
    try {
      const passwordValidation = SecurityUtils.validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        return {
          success: false,
          error: {
            code: 'weak-password',
            message: passwordValidation.errors.join(', '),
          },
        };
      }

      await confirmPasswordReset(auth, oobCode, newPassword);
      return { success: true, message: 'Your password has been updated. You can sign in now.' };
    } catch (error: any) {
      console.error('Confirm password reset error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getActionCodeErrorMessage(error.code),
        },
      };
    }
  }

  /** Apply an email-verification oobCode. */
  async verifyEmail(oobCode: string): Promise<AuthResult> {
    try {
      await applyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      return { success: true, message: 'Your email has been verified. You can continue using your account.' };
    } catch (error: any) {
      console.error('Verify email error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getActionCodeErrorMessage(error.code),
        },
      };
    }
  }

  private getActionCodeErrorMessage(errorCode: string): string {
    const messages: { [key: string]: string } = {
      'auth/expired-action-code': 'This link has expired. Please request a new one.',
      'auth/invalid-action-code': 'This link is invalid or has already been used.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account was found for this link.',
      'auth/weak-password': 'Password is too weak',
    };
    return messages[errorCode] || 'This link is invalid or has expired. Please request a new one.';
  }

  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Get user data from Firestore
  async getCurrentUserData(): Promise<AuthUser | null> {
    try {
      const user = this.getCurrentUser();
      if (!user) return null;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) return null;

      return userDoc.data() as AuthUser;
    } catch (error) {
      console.error('Get user data error:', error);
      return null;
    }
  }

  // Update user profile
  async updateUserProfile(updates: Partial<AuthUser>): Promise<AuthResult> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        return {
          success: false,
          error: {
            code: 'not-authenticated',
            message: 'User not authenticated'
          }
        };
      }

      // Sanitize updates
      const sanitizedUpdates: Partial<AuthUser> = {};
      if (updates.name) sanitizedUpdates.name = SecurityUtils.sanitizeString(updates.name);
      if (updates.phone) sanitizedUpdates.phone = SecurityUtils.sanitizeString(updates.phone);

      // Update Firestore document
      await setDoc(doc(db, 'users', user.uid), sanitizedUpdates, { merge: true });

      // Update Firebase Auth profile if name changed
      if (updates.name) {
        await updateProfile(user, {
          displayName: sanitizedUpdates.name
        });
      }

      return { success: true };
    } catch (error: any) {
      console.error('Update profile error:', error);
      return {
        success: false,
        error: {
          code: error.code || 'unknown-error',
          message: this.getErrorMessage(error.code)
        }
      };
    }
  }

  // Check if user is admin
  async isAdmin(): Promise<boolean> {
    try {
      const userData = await this.getCurrentUserData();
      return userData?.role === 'admin';
    } catch (error) {
      console.error('Check admin error:', error);
      return false;
    }
  }

  // Get error message from Firebase error code
  private getErrorMessage(errorCode: string): string {
    const errorMessages: { [key: string]: string } = {
      'auth/user-not-found': 'Invalid email or password',
      'auth/wrong-password': 'Invalid email or password',
      'auth/email-already-in-use': 'An account with this email already exists',
      'auth/weak-password': 'Password is too weak',
      'auth/invalid-email': 'Invalid email address',
      'auth/user-disabled': 'This account has been disabled',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'auth/invalid-credential': 'Invalid email or password',
      'validation-error': 'Please check your input and try again'
    };

    return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
